import React, { useState, useEffect } from 'react';
import './App.css';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { LanguageProvider } from './contexts/LanguageContext';
import { FontSizeProvider } from './contexts/FontSizeContext';
import { DisplayProvider } from './contexts/DisplayContext';
import realtimeService from './services/realtimeService';
import ShoppingListManager from './components/ShoppingListManager';
import AuthModal from './components/AuthModal';
import { ShoppingBag, Sparkles } from 'lucide-react';

const AppContent = () => {
  const { user, loading, connectionError, logout } = useAuth();
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [managerReady, setManagerReady] = useState(() => {
    // Check if manager was previously ready (persist across language changes)
    return localStorage.getItem('listWizManagerReady') === 'true';
  });
  const [hasInitialized, setHasInitialized] = useState(false);

  useEffect(() => {
    console.log('🔄 App: User state changed:', user ? 'logged in' : 'not logged in');
    console.log('🔄 App: Loading:', loading, 'Connection Error:', connectionError, 'Manager Ready:', managerReady);
    
    // Mark as initialized once we've finished the initial auth check
    if (!loading && !hasInitialized) {
      setHasInitialized(true);
      console.log('🔄 App: Initial auth check completed');
    }
    
    if (!loading && !user && !connectionError) {
      setShowAuthModal(true);
      // Reset manager ready state when user logs out
      setManagerReady(false);
      localStorage.removeItem('listWizManagerReady');
      // Disconnect real-time service when logged out
      realtimeService.disconnect();
    } else if (user) {
      // Hide modal when user is authenticated
      setShowAuthModal(false);
      // Connect real-time service when logged in
      realtimeService.connect();
    }
  }, [user, loading, connectionError, hasInitialized]);

  if (loading) {
    console.log('🔄 App: Showing loading screen - loading:', loading, 'user:', !!user, 'managerReady:', managerReady);
    return (
      <div className="loading-screen">
        <div className="loading-content">
          <div className="loading-icon">
            <ShoppingBag size={48} className="bounce" />
            <Sparkles size={24} className="sparkle" />
          </div>
          <h2>Loading ListWiz</h2>
          <div className="loading-bar">
            <div className="loading-progress"></div>
          </div>
        </div>
      </div>
    );
  }

  if (connectionError) {
    return (
      <div className="connection-error-screen">
        <div className="error-content">
          <div className="error-icon">
            <ShoppingBag size={64} />
          </div>
          <h1>Backend Server Not Running</h1>
          <p>The shopping list backend server is not accessible.</p>
          <div className="error-details">
            <h3>To fix this issue:</h3>
            <ol>
              <li>Open a terminal in the project directory</li>
              <li>Navigate to the backend folder: <code>cd backend</code></li>
              <li>Install dependencies: <code>npm install</code></li>
              <li>Start the server: <code>npm start</code></li>
            </ol>
            <p>The server should start on <strong>http://localhost:3001</strong></p>
          </div>
          <button 
            onClick={() => window.location.reload()}
            className="retry-btn"
          >
            Retry Connection
          </button>
        </div>
      </div>
    );
  }

  if (!user) {
    return (
      <>
        <div className="auth-landing">
          <div className="auth-landing-content">
            <div className="landing-icon">
              <ShoppingBag size={64} />
            </div>
            <h1>ListWiz</h1>
            <p>Create, share, and manage your lists with ease</p>
            <button 
              onClick={() => setShowAuthModal(true)}
              className="get-started-btn"
            >
              Get Started
            </button>
          </div>
        </div>
        
        {showAuthModal && (
          <AuthModal
            onClose={() => setShowAuthModal(false)}
          />
        )}
      </>
    );
  }

  console.log('🔄 App: About to render ShoppingListManager');
  console.log('🔄 App: Current conditions - user:', !!user, 'loading:', loading, 'connectionError:', connectionError, 'managerReady:', managerReady, 'hasInitialized:', hasInitialized);
  
  // Only show loading overlay for the very first time when user logs in and manager is not ready
  const showLoadingOverlay = user && !managerReady && hasInitialized;
  
  try {
    return (
      <div className="app-container">
        {showLoadingOverlay && (
          <div className="loading-overlay">
            <div className="loading-content">
              <div className="loading-icon">
                <ShoppingBag size={48} className="bounce" />
                <Sparkles size={24} className="sparkle" />
              </div>
              <h2>Setting up your lists...</h2>
              <div className="loading-bar">
                <div className="loading-progress"></div>
              </div>
            </div>
          </div>
        )}
        <ShoppingListManager 
          user={user} 
          onLogout={logout} 
          onReady={() => {
            console.log('✅ App: Manager is ready!');
            setManagerReady(true);
            localStorage.setItem('listWizManagerReady', 'true');
          }}
        />
      </div>
    );
  } catch (error) {
    console.error('🔄 App: Error rendering ShoppingListManager:', error);
    return <div>Error rendering ShoppingListManager: {error.message}</div>;
  }
};

function App() {
  return (
    <AuthProvider>
      <LanguageProvider>
        <FontSizeProvider>
          <DisplayProvider>
            <div className="App">
              <AppContent />
            </div>
          </DisplayProvider>
        </FontSizeProvider>
      </LanguageProvider>
    </AuthProvider>
  );
}

export default App;
